import { FLOW_MODES } from '@avanta/shared/constants/appointments';
import { getTimeSlotsForDate } from '@avanta/shared/utils/appointments';
import { cn } from '@avanta/shared/utils/cn';
import { extractTimeFromUnified } from '@avanta/shared/utils/date';
import { useFormContext, useWatch } from 'react-hook-form';
import { SessionSummary } from 'pages/AppointmentsReserve/components/ui/SessionSummary';
import { StepBackButton } from 'pages/AppointmentsReserve/components/ui/StepBackButton';
import { StepNextButton } from 'pages/AppointmentsReserve/components/ui/StepNextButton';
import { AppointmentsReservePayload } from 'pages/AppointmentsReserve/constants';
import { useFetchInsuredCenters } from 'pages/AppointmentsReserve/hooks/useFetchInsuredCenters';
import { type AppointmentsReserveFormData, type StepProps } from 'pages/AppointmentsReserve/types';
import { hasFixedPsychologist } from 'pages/AppointmentsReserve/utils';
import { AppointmentsCalendar } from './components/AppointmentsCalendar';
import { SearchPsychologistBanner } from './components/SearchPsychologistBanner';
import { SessionStepSkeleton } from './components/SessionStepSkeleton';
import { useFetchTopPsychologists } from './hooks/useFetchTopPsychologists';
import { useSessionAvailability } from './hooks/useSessionAvailability';

export const SessionStep = ({ onNext, onBack }: StepProps): React.ReactElement => {
  const { control, setValue } = useFormContext<AppointmentsReserveFormData>();
  const { isLoading: isSessionDataLoading } = useFetchInsuredCenters();
  const { psychologist: psychologistData, isLoading: isPsychologistLoading } =
    useFetchTopPsychologists();
  const [backofficeCenterId, dateTime, psychologistForm, flow] = useWatch({
    control,
    name: [
      AppointmentsReservePayload.backofficeCenterId,
      AppointmentsReservePayload.dateTime,
      AppointmentsReservePayload.psychologist,
      AppointmentsReservePayload.flow,
    ],
  });
  const isEditFlow = flow === FLOW_MODES.edit;
  const { availableDates, isFetching, isError } = useSessionAvailability();

  const psychologist = hasFixedPsychologist(flow) ? psychologistForm : psychologistData;
  const isSelectedSlotAvailable = Boolean(
    dateTime &&
    getTimeSlotsForDate(new Date(dateTime), availableDates).timeSlots.some(
      slot => !slot.disabled && slot.time === extractTimeFromUnified(dateTime)
    )
  );
  const canContinue =
    !!psychologist && isSelectedSlotAvailable && !isFetching && !isError && !isPsychologistLoading;

  const handleContinue = (): void => {
    if (!canContinue || !psychologist) return;
    setValue(AppointmentsReservePayload.psychologist, psychologist);
    onNext();
  };

  const handleBack = (): void => {
    setValue(AppointmentsReservePayload.dateTime, '');
    setValue(AppointmentsReservePayload.psychologist, undefined);
    onBack();
  };

  if (isSessionDataLoading) {
    return <SessionStepSkeleton />;
  }

  return (
    <div className="flex flex-col gap-8 max-w-241 w-full">
      {!dateTime && <SearchPsychologistBanner />}
      <div className="flex gap-8">
        <SessionSummary />
        {backofficeCenterId && <AppointmentsCalendar />}
      </div>
      <div className={cn('flex w-full justify-between', isEditFlow && 'justify-end')}>
        {!isEditFlow && <StepBackButton onClick={handleBack} />}
        <StepNextButton
          disabled={!canContinue}
          isLoading={isPsychologistLoading}
          onClick={handleContinue}
        />
      </div>
    </div>
  );
};
